// ========================= Config =========================
const collectionHandles = ['all-prods-except-mags-merch']; // One or more collection handles to check
const productVariantId = 51213152551193; // The product variant you want to auto-add/remove
const startDate = new Date('2025-09-29T00:00:00');  // Promo start
const endDate = new Date('2025-10-01T00:00:00');    // Promo end
const isDevMode = true; // Verbose logging

// Minimum total QUANTITY in the cart from ANY of the target collections
// e.g., if set to 2, two of the same qualifying item also satisfies the requirement.
const itemsFromCollection = 4;

// ========================= Helpers =========================
function devLog(message, ...args) {
  if (isDevMode) console.log(message, ...args);
}

function isWithinDateRange() {
  const now = new Date();
  devLog(`Checking date ${now} in range: ${startDate} - ${endDate}`);
  return now >= startDate && now <= endDate;
}

// ========================= Cart I/O =========================
function fetchCartData() {
  devLog('Starting to fetch cart data...');
  const start = performance.now();

  return fetch('/cart.js')
    .then(r => r.json())
    .then(cart => {
      const end = performance.now();
      devLog(`Cart data fetched in ${(end - start).toFixed(2)} ms`, cart);
      return cart;
    })
    .catch(err => {
      devLog('Error fetching cart data:', err);
      return null;
    });
}

function isProductInCart(cart, variantId) {
  if (!cart || !cart.items) return false;
  return cart.items.some(item => item.variant_id === variantId);
}

function addProductToCart(variantId) {
  devLog('Adding product to cart with variantId:', variantId);
  const start = performance.now();

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', 1);

  return fetch('/cart/add.js', { method: 'POST', body: formData })
    .then(r => r.json())
    .then(data => {
      const end = performance.now();
      devLog(`Product added to cart in ${(end - start).toFixed(2)} ms`, data);
      return data;
    })
    .catch(err => {
      devLog('Error adding product to cart:', err);
      return null;
    });
}

function removeProductFromCart(variantId) {
  devLog('Removing product from cart with variantId:', variantId);
  const start = performance.now();

  // Use /cart/update.js to directly zero out the variant quantity
  const formData = new FormData();
  formData.append(`updates[${variantId}]`, 0);

  return fetch('/cart/update.js', { method: 'POST', body: formData })
    .then(r => r.json())
    .then(data => {
      const end = performance.now();
      devLog(`Product removed from cart in ${(end - start).toFixed(2)} ms`, data);
      return data;
    })
    .catch(err => {
      devLog('Error removing product from cart:', err);
      return null;
    });
}

// ========================= Collections Check =========================
function checkProductInCollections(sku, handles) {
  const targetHandles = Array.isArray(handles) ? handles : [handles];
  devLog(`Checking collections for SKU: ${sku} against:`, targetHandles);

  const start = performance.now();
  const url = `https://foxshopify.com:7777/api/shopifygql/productcollections?webstore=foxchapel.myshopify.com&sku=${sku}`;
  devLog(`Calling collection API: ${url}`);

  return fetch(url)
    .then(r => r.json())
    .then(data => {
      const end = performance.now();
      devLog(`Collection data for SKU ${sku} fetched in ${(end - start).toFixed(2)} ms`, data);

      const productCollections = Array.isArray(data.collections) ? data.collections : [];
      const inAny = targetHandles.some(h => productCollections.includes(h));
      devLog(`SKU ${sku} in any target collection:`, inAny);
      return inAny;
    })
    .catch(err => {
      devLog(`Error fetching collection data for SKU ${sku}:`, err);
      return false;
    });
}

// Sum *quantities* of items whose SKU is in ANY of the target collections
function checkCartItemsInCollection(cart, handles, minItemsRequired = 1) {
  devLog(`Checking if cart contains quantity ≥ ${minItemsRequired} from any of:`, Array.isArray(handles) ? handles : [handles]);

  if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
    devLog('Cart is empty or invalid while checking collection membership.');
    return Promise.resolve(false);
  }

  // SKU -> total quantity across cart
  const skuTotals = cart.items.reduce((acc, item) => {
    const sku = item.sku;
    const qty = Number(item.quantity) || 0;
    if (!sku) {
      devLog('Item missing SKU; skipping:', item);
      return acc;
    }
    acc[sku] = (acc[sku] || 0) + qty;
    return acc;
  }, {});

  const uniqueSkus = Object.keys(skuTotals);
  if (uniqueSkus.length === 0) {
    devLog('No SKUs found to check.');
    return Promise.resolve(false);
  }

  const checks = uniqueSkus.map(sku =>
    checkProductInCollections(sku, handles).then(inAnyCollection => ({ sku, inAnyCollection }))
  );

  return Promise.all(checks).then(results => {
    const totalQtyInCollections = results.reduce((sum, { sku, inAnyCollection }) => {
      return sum + (inAnyCollection ? (skuTotals[sku] || 0) : 0);
    }, 0);

    const meetsThreshold = totalQtyInCollections >= minItemsRequired;
    devLog(
      `Total quantity in target collections: ${totalQtyInCollections}. ` +
      `Required: ${minItemsRequired}. Meets threshold: ${meetsThreshold}`
    );

    return meetsThreshold;
  });
}

// ========================= Orchestration =========================
function ensurePromoProductState(cart) {
  // Only enforce inside the active date window
  if (!isWithinDateRange()) {
    devLog('Outside active date range; no add/remove actions.');
    return;
  }

  const promoInCart = isProductInCart(cart, productVariantId);

  checkCartItemsInCollection(cart, collectionHandles, itemsFromCollection)
    .then(meetsThreshold => {
      if (meetsThreshold && !promoInCart) {
        devLog(`Threshold met (≥ ${itemsFromCollection}), promo NOT in cart → adding.`);
        return addProductToCart(productVariantId);
      }
      if (!meetsThreshold && promoInCart) {
        devLog(`Threshold NOT met (< ${itemsFromCollection}), promo IS in cart → removing.`);
        return removeProductFromCart(productVariantId);
      }
      devLog('No action needed (state already correct).');
      return null;
    })
    .catch(err => devLog('Error ensuring promo product state:', err));
}

// Throttled re-check after cart changes
let recheckTimer = null;
function scheduleRecheck() {
  if (recheckTimer) clearTimeout(recheckTimer);
  recheckTimer = setTimeout(() => {
    fetchCartData().then(cart => {
      if (cart) ensurePromoProductState(cart);
    });
  }, 100);
}

// Watch for cart mutations (add/change/update via fetch)
let watcherInstalled = false;
function setupCartChangeWatcher() {
  if (watcherInstalled) return;
  watcherInstalled = true;

  const originalFetch = window.fetch;
  window.fetch = function(input, init) {
    const req = (typeof input === 'string') ? input : (input && input.url) || '';
    const isCartEndpoint = /\/cart\/(add|change|update)\.js(\?|$)/.test(req);

    return originalFetch.apply(this, arguments).then(res => {
      if (isCartEndpoint) {
        // Re-check after the cart endpoint completes
        res.clone().text().finally(() => scheduleRecheck());
      }
      return res;
    });
  };

  devLog('Cart change watcher installed.');
}

// ========================= Entry =========================
function checkCartOnPageLoad() {
  devLog('Checking the cart on page load...');

  if (!isWithinDateRange()) {
    devLog('Outside the active date range. No further actions required.');
    return;
  }

  setupCartChangeWatcher();

  fetchCartData().then(cart => {
    if (!cart || !cart.items || cart.items.length === 0) {
      devLog('The cart is empty. No further actions required.');
      return;
    }

    ensurePromoProductState(cart);
  });
}

window.addEventListener('load', checkCartOnPageLoad);
